import { Chip } from "@/components/Chip";
import { analyzeFolio, formatFecha } from "@/lib/folio-analysis";
import type { Folio } from "@/lib/types";

export function DashboardHero({ folio }: { folio: Folio }) {
  const status = analyzeFolio(folio.movimientos);

  return (
    <header className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-500">
            Folio registral
          </p>
          <h1 className="mt-1 font-mono text-3xl font-semibold tracking-tight text-slate-900">
            {folio.principal.folioRegistral}
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            {folio.principal.municipio.replace(/^\d+\s+/, "").toLowerCase()
              .replace(/\b\w/g, (c) => c.toUpperCase())}, {folio.principal.estado.charAt(0)}
            {folio.principal.estado.slice(1).toLowerCase()}
          </p>
        </div>
        <div className="text-right text-xs text-slate-500">
          Consultado el{" "}
          <span className="font-medium text-slate-700">
            {new Date().toLocaleDateString("es-MX", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })}
          </span>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Chip tone={status.gravamenActivo ? "emerald" : "rose"} dot>
          {status.gravamenActivo ? "Gravamen activo" : "Sin gravámenes vigentes"}
        </Chip>
        <Chip tone="slate">
          {status.totalMovimientos} movimiento{status.totalMovimientos === 1 ? "" : "s"}
        </Chip>
        {status.avisosVigentes > 0 && (
          <Chip tone="amber" dot>
            {status.avisosVigentes} aviso{status.avisosVigentes === 1 ? "" : "s"} vigente
            {status.avisosVigentes === 1 ? "" : "s"}
          </Chip>
        )}
        {status.ultimoMovimientoFecha && (
          <Chip tone="indigo">
            Último: {status.ultimoMovimientoActo} ·{" "}
            {formatFecha(status.ultimoMovimientoFecha)}
          </Chip>
        )}
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-5 text-sm sm:grid-cols-4">
        <Stat
          label="Gravámenes"
          value={status.conteoPorCategoria.gravamen}
          tone="emerald"
        />
        <Stat
          label="Cancel. gravamen"
          value={status.cancelacionesGravamen}
          tone="rose"
        />
        <Stat label="Avisos" value={status.conteoPorCategoria.aviso} tone="amber" />
        <Stat
          label="Compraventas"
          value={status.conteoPorCategoria.compraventa}
          tone="indigo"
        />
      </dl>
    </header>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "rose" | "emerald" | "amber" | "indigo";
}) {
  const color = {
    rose: "text-rose-600",
    emerald: "text-emerald-600",
    amber: "text-amber-600",
    indigo: "text-indigo-600",
  }[tone];
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className={`mt-1 text-2xl font-semibold tabular-nums ${color}`}>
        {value}
      </dd>
    </div>
  );
}
